import type { ContractMilestone } from '../types';

/**
 * CR1-B (the 117th, his BUILD) - where a contract's deadlines LAND. The review screen added every offset to escrow
 * open, so the 1101 PSA scored 4 of 10: "the day after the Examination Period ends + 120" landed at escrow open + 120,
 * Closing landed before Planning Approval, and business days counted as calendar days.
 *
 * ⭐ The rules, each proved on the 1101 PSA and his verified tracker:
 * - "beginning on X and extending through N days thereafter" ends X + N; "beginning the day AFTER Y expires" starts at
 *   Y's end + 1 (Planning Approval: 2026-01-21 + 120 = 2026-05-21).
 * - Each extension is a FRESH period beginning the day after the last one ends: + 1 + days. (07-22 + 30 + 30 = 09-20,
 *   a Sunday, would roll to 09-21; his tracker says 09-22.) An intermediate end never rolls - only the final one.
 * - A chained period counts from the other's end AS EXTENDED, unless the contract says "initial" (Release #3).
 * - Business days skip weekends and federal holidays; the weekend roll applies only when the contract has one.
 * ⛔ A day count with nothing to count from gets NO date - "10 days after receipt of the objections" is an event.
 */
/**
 * `root` is what the date ultimately counts from: escrow open (it moves when escrow open is re-set) or a date the contract
 * states (it never does). Apply sends a calendar offset only for an escrow root.
 */
export interface ResolvedContractDate { date: string | null; needs: string | null; root?: 'escrow' | 'explicit' | null }

/** Removable buyer contingencies - "close N days after the contingencies are removed" counts from the LATEST of these. */
const CONTINGENCY_TYPES = ['due_diligence', 'loan', 'appraisal', 'title'];
/** Ten years. A longer count is a misread, and an unbounded one froze the screen (the review's probe: 2,000,000 business days). */
const MAX_DAYS = 3650;

const DAY = 86_400_000;
const toMs = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => toIso(toMs(iso) + n * DAY);

/** The nth (1-based) given weekday of a month; n = -1 is the last. Weekday 0 = Sunday. */
const nthWeekday = (year: number, month: number, weekday: number, n: number): string => {
  if (n > 0) {
    const first = new Date(Date.UTC(year, month, 1)).getUTCDay();
    return toIso(Date.UTC(year, month, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7));
  }
  const lastDay = new Date(Date.UTC(year, month + 1, 0));
  return toIso(Date.UTC(year, month, lastDay.getUTCDate() - ((lastDay.getUTCDay() - weekday + 7) % 7)));
};

/** A fixed-date holiday as observed: a Saturday holiday on the Friday before, a Sunday one on the Monday after. */
const observed = (iso: string): string => {
  const dow = new Date(toMs(iso)).getUTCDay();
  return dow === 6 ? addDays(iso, -1) : dow === 0 ? addDays(iso, 1) : iso;
};

const holidayCache = new Map<number, Set<string>>();
/** The federal holidays of a year, as observed (the bank holidays a contract's "business day" skips). */
const federalHolidays = (year: number): Set<string> => {
  const hit = holidayCache.get(year);
  if (hit) return hit;
  const y = String(year);
  const set = new Set<string>([
    observed(`${y}-01-01`),
    nthWeekday(year, 0, 1, 3), // Martin Luther King Jr. Day
    nthWeekday(year, 1, 1, 3), // Washington's Birthday
    nthWeekday(year, 4, 1, -1), // Memorial Day
    observed(`${y}-06-19`),
    observed(`${y}-07-04`),
    nthWeekday(year, 8, 1, 1), // Labor Day
    nthWeekday(year, 9, 1, 2), // Columbus Day
    observed(`${y}-11-11`),
    nthWeekday(year, 10, 4, 4), // Thanksgiving
    observed(`${y}-12-25`),
  ]);
  // Next New Year's Day observed on Dec 31 of this year.
  set.add(observed(`${year + 1}-01-01`));
  holidayCache.set(year, set);
  return set;
};

export const isBusinessDay = (iso: string): boolean => {
  const dow = new Date(toMs(iso)).getUTCDay();
  return dow !== 0 && dow !== 6 && !federalHolidays(+iso.slice(0, 4)).has(iso.slice(0, 10));
};

/** n business days after (or, negative, before) a date. */
export const addBusinessDays = (iso: string, n: number): string => {
  let d = iso.slice(0, 10);
  const step = n < 0 ? -1 : 1;
  for (let left = Math.abs(n); left > 0;) {
    d = addDays(d, step);
    if (isBusinessDay(d)) left -= 1;
  }
  return d;
};

/** A real calendar date: "2026-13-45" passes a shape test and is no date at all. */
const isRealDate = (iso: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(iso) && !Number.isNaN(toMs(iso)) && toIso(toMs(iso)) === iso;

export const rollToBusinessDay = (iso: string): string => {
  let d = iso.slice(0, 10);
  for (let i = 0; i < 31 && !isBusinessDay(d); i += 1) d = addDays(d, 1);
  return d;
};

const NEEDS = 'needs a date - it counts from an event, not a date';

export const resolveContractDates = (
  milestones: ContractMilestone[],
  anchor: string,
  opts: { weekendRoll: boolean; extensionsTaken?: Record<number, number> },
): ResolvedContractDate[] => {
  const key = (s: string) => s.trim().toLowerCase();
  const memo = new Map<string, ResolvedContractDate>();

  const resolve = (i: number, initialOnly: boolean, stack: Set<number>): ResolvedContractDate => {
    const memoKey = `${i}|${initialOnly}`;
    const known = memo.get(memoKey);
    if (known) return known;
    const m = milestones[i];
    const done = (r: ResolvedContractDate) => { memo.set(memoKey, r); return r; };

    if (m.explicit_date) {
      const d = m.explicit_date.slice(0, 10);
      return done(isRealDate(d) ? { date: d, needs: null, root: 'explicit' } : { date: null, needs: `needs a date - "${d}" is not a date`, root: null });
    }
    if (m.offset_days == null) return done({ date: null, needs: 'needs a date - no day count', root: null });
    if (Math.abs(m.offset_days) > MAX_DAYS) return done({ date: null, needs: `needs a date - ${m.offset_days} days is out of range`, root: null });

    let base: string | null = null;
    let root: 'escrow' | 'explicit' | null = null;
    if (m.offset_from === 'escrow_open' || m.offset_from === 'effective_date') {
      if (!anchor) return done({ date: null, needs: 'needs the escrow open date', root: null });
      base = anchor.slice(0, 10);
      root = 'escrow';
    } else if (m.offset_from === 'milestone' && m.after_milestone) {
      const j = milestones.findIndex((x, k) => k !== i && key(x.label) === key(m.after_milestone!));
      if (j < 0) return done({ date: null, needs: `needs a date - "${m.after_milestone}" is not in this list`, root: null });
      if (stack.has(j)) return done({ date: null, needs: 'needs a date - it counts from itself', root: null });
      const before = resolve(j, !!m.from_initial_end, new Set([...stack, i]));
      if (!before.date) return done({ date: null, needs: `needs a date - "${m.after_milestone}" has none`, root: null });
      base = before.date;
      root = before.root ?? null;
    } else if (m.offset_from === 'contingency_removal') {
      // ⭐ The LATEST resolved contingency, wherever it counts from - a chained loan contingency included. The backend
      //   used to add up day counts as if every one counted from escrow open (90 + 30 put the 1101 Closing before PAP).
      let latest: ResolvedContractDate | null = null;
      milestones.forEach((x, k) => {
        if (k === i || stack.has(k) || !CONTINGENCY_TYPES.includes(x.deadline_type) || x.offset_from === 'contingency_removal') return;
        const r = resolve(k, false, new Set([...stack, i]));
        if (r.date && (!latest || r.date > latest.date!)) latest = r;
      });
      const found = latest as ResolvedContractDate | null;
      if (!found) return done({ date: null, needs: 'needs a date - no contingency has a date to count from', root: null });
      base = found.date as string;
      root = found.root ?? null;
    } else if (m.offset_from === 'agreement_date' || m.offset_from === 'amendment_date') {
      // DOCTRACK (F5): an amendment counts from the agreement's date or its own; part 2 places these. Until then the row
      // says what it counts from and asks for the date - never escrow open plus its days.
      const what = m.offset_from === 'agreement_date' ? "the agreement's date" : "the amendment's date";
      return done({ date: null, needs: `needs a date - it counts from ${what}`, root: null });
    } else {
      return done({ date: null, needs: NEEDS, root: null });
    }

    const start = m.starts_day_after ? addDays(base, 1) : base;
    let end = m.day_unit === 'business' ? addBusinessDays(start, m.offset_days) : addDays(start, m.offset_days);
    const options = m.extension_options;
    if (!initialOnly && options && options.count > 0) {
      const taken = Math.max(0, Math.min(options.count, Math.trunc(opts.extensionsTaken?.[i] ?? 0)));
      end = addDays(end, taken * (1 + options.days));
    }
    if (opts.weekendRoll) end = rollToBusinessDay(end);
    return done({ date: end, needs: null, root });
  };

  return milestones.map((_, i) => resolve(i, false, new Set([i])));
};
